import React, { Fragment, useEffect, useState } from 'react';
import '../styles/RecentSubmissions.css';

const headers = {
  team: { title: 'Team Summary', label: 'Opponent', idLabel: null },
  player: { title: 'Player Stats', label: 'Opponent', idLabel: null },
  enemy: { title: 'Enemy Scouting', label: 'Opponent', idLabel: null }
};

const getGridTemplateColumns = (key, meta) => {
  if (meta.idLabel) return '1fr 0.8fr 1fr 1fr 1fr 0.6fr';
  if (key === 'enemy') return '1fr 0.75fr 1fr 1fr 1fr 0.72fr';
  return '1.05fr 0.8fr 1fr 1fr 0.72fr';
};

const EMPTY = '-';
const DEFAULT_VISIBLE_COUNT = 20;
const SHOW_MORE_STEP = 20;
const MODE_TABS = [
  { value: 'ALL', label: 'All Modes' },
  { value: 'HP', label: 'Hardpoint' },
  { value: 'SND', label: 'Search & Destroy' },
  { value: 'CTL', label: 'Control' },
];
const SECTION_KEYS = ['team', 'player', 'enemy'];
const PLAYER_DETAIL_FIELDS = [
  ['operator', 'Operator'],
  ['score', 'Score'],
  ['K', 'K'],
  ['D', 'D'],
  ['A', 'A'],
  ['HillTime', 'Hill Time'],
  ['Impact', 'Impact'],
  ['TotalDamage', 'Total Damage'],
  ['CaptureKills', 'Capture Kills'],
  ['DistanceMoved', 'Distance Moved'],
  ['OperatorValue', 'Operator Value'],
  ['ADR', 'ADR'],
  ['FirstBloods', 'First Bloods'],
  ['Clutches1v1', 'Clutches 1v1'],
  ['Damage', 'Damage'],
];

const normalizeMode = (rawMode) => {
  const value = String(rawMode || '').toUpperCase();
  if (value.includes('SND') || value.includes('SEARCH')) return 'SND';
  if (value.includes('CTL') || value.includes('CTRL') || value.includes('CONTROL')) return 'CTL';
  if (value.includes('HP') || value.includes('HARDPOINT')) return 'HP';
  return null;
};

const inferModeFromItem = (item) => {
  const direct = normalizeMode(item?.mode || item?.Mode || item?.modeId || item?.ModeID);
  if (direct) return direct;

  const source = String(
    item?.matchId ||
    item?.ModeID ||
    item?.modeId ||
    item?.id ||
    item?.path ||
    ''
  ).toLowerCase();
  if (source.includes('mode_snd') || source.includes('snd')) return 'SND';
  if (source.includes('mode_ctl') || source.includes('mode_ctrl') || source.includes('ctl') || source.includes('ctrl') || source.includes('control')) return 'CTL';
  if (source.includes('mode_hp') || source.includes('hp') || source.includes('hardpoint')) return 'HP';
  return null;
};

const getModeLabel = (modeKey) => {
  if (modeKey === 'HP') return 'Hardpoint';
  if (modeKey === 'SND') return 'Search & Destroy';
  if (modeKey === 'CTL') return 'Control';
  return EMPTY;
};

const getSortTs = (item) => {
  if (typeof item?.sortTs === 'number' && Number.isFinite(item.sortTs)) return item.sortTs;
  const parsed = new Date(item?.rawDate || item?.date || '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildFilteredItems = (items, mode) => {
  const sorted = [...items].sort((a, b) => getSortTs(b) - getSortTs(a));
  if (mode === 'ALL') return sorted;
  return sorted.filter((item) => inferModeFromItem(item) === mode);
};

const displayValue = (value) => {
  if (value === 0) return '0';
  return value || EMPTY;
};

const getItemKey = (sectionKey, item) =>
  `${sectionKey}:${item.seasonId || 'root'}:${item.matchId || item.id}`;

const buildScoreLabel = (item) => {
  const hasScoreFor = item.scoreFor === 0 || (item.scoreFor !== undefined && item.scoreFor !== null && String(item.scoreFor).trim() !== '');
  const hasScoreAgainst = item.scoreAgainst === 0 || (item.scoreAgainst !== undefined && item.scoreAgainst !== null && String(item.scoreAgainst).trim() !== '');
  return hasScoreFor && hasScoreAgainst ? `${item.scoreFor} - ${item.scoreAgainst}` : '';
};

const buildMetaRows = (item) => ([
  ['Date', item.date],
  ['Opponent', item.opponent || item.vs],
  ['Opponent Region', item.opponentRegion],
  ['Mode', getModeLabel(inferModeFromItem(item))],
  ['Map', item.map || item.Map],
  ['Match Category', item.matchCategory],
  ['Competition Region', item.competitionRegion],
  ['Competition', item.competition],
  ['Result', item.result],
  ['Score', buildScoreLabel(item)],
  ['Notes', item.notes],
]).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');

const renderMatchDetails = (item, sectionKey) => (
  <div className="recent-submissions__detail-grid">
    {sectionKey === 'enemy' && item.team && (
      <div>
        <span>Scouted Team</span>
        <strong>{item.team}</strong>
      </div>
    )}
    {buildMetaRows(item).map(([label, value]) => (
      <div key={label} className={label === 'Notes' ? 'is-wide' : ''}>
        <span>{label}</span>
        <strong>{displayValue(value)}</strong>
      </div>
    ))}
  </div>
);

const renderPlayerDetails = (item) => {
  const players = Array.isArray(item.players) ? item.players : [];
  if (!players.length) {
    return <div className="recent-submissions__detail-empty">No player detail rows available for this submission.</div>;
  }

  return (
    <div className="recent-submissions__player-detail">
      {players.map((player, idx) => {
        const fields = PLAYER_DETAIL_FIELDS
          .filter(([key]) => player[key] !== undefined && player[key] !== null && String(player[key]).trim() !== '');

        return (
          <div key={`${player.player || 'player'}-${idx}`} className="recent-submissions__player-card">
            <div className="recent-submissions__player-name">
              <span>Player</span>
              <strong>{player.player || EMPTY}</strong>
            </div>
            <div className="recent-submissions__player-stats">
              {fields.map(([key, label]) => (
                <div key={key}>
                  <span>{label}</span>
                  <strong>{displayValue(player[key])}</strong>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecentSubmissions = ({
  loading,
  sections,
  onEdit,
  onDelete,
  canDelete,
  sectionFilter = 'team',
  seasonOptions = [],
  selectedSeason = '',
  onSeasonChange = () => {},
}) => {
  const [filtersBySection, setFiltersBySection] = useState(() => ({
    team: { mode: 'ALL' },
    player: { mode: 'ALL' },
    enemy: { mode: 'ALL' },
  }));
  const [collapsedBySection, setCollapsedBySection] = useState(() => ({
    team: false,
    player: false,
    enemy: false,
  }));
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState(() => ({
    team: DEFAULT_VISIBLE_COUNT,
    player: DEFAULT_VISIBLE_COUNT,
    enemy: DEFAULT_VISIBLE_COUNT,
  }));
  const normalizedSeasonOptions = (seasonOptions && seasonOptions.length > 0)
    ? seasonOptions
    : (selectedSeason ? [{ value: selectedSeason, label: selectedSeason }] : []);

  useEffect(() => {
    setExpandedSubmissionId(null);
    setVisibleCounts({
      team: DEFAULT_VISIBLE_COUNT,
      player: DEFAULT_VISIBLE_COUNT,
      enemy: DEFAULT_VISIBLE_COUNT,
    });
  }, [selectedSeason, sectionFilter, filtersBySection]);

  const updateSectionFilter = (sectionKey, patch) => {
    setFiltersBySection((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        ...patch,
      },
    }));
  };
  const toggleSection = (sectionKey) => {
    setCollapsedBySection((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const getEmptyCopy = (items, mode) => {
    if (!items.length) {
      return {
        title: 'No submissions yet',
        text: 'No submissions were found for this season.',
      };
    }
    if (mode !== 'ALL') {
      return {
        title: `No ${getModeLabel(mode)} submissions`,
        text: `No ${getModeLabel(mode)} submissions were found in this season.`,
      };
    }
    return {
      title: 'No submissions found',
      text: 'No recent submissions match the current filters.',
    };
  };

  return (
    <div className="recent-submissions">
      <div className="recent-submissions__header">
        <div className="recent-submissions__intro">
          <h2>Recent submissions</h2>
          <p>
            Review the latest match records, expand submissions for details, or open the original form in edit mode.
          </p>
        </div>
        <label className="recent-submissions__season">
          <span>Season</span>
          <select
            value={selectedSeason}
            onChange={(e) => onSeasonChange(e.target.value)}
          >
            {normalizedSeasonOptions.map((season) => (
              <option key={season.value} value={season.value}>
                {season.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="recent-submissions__loading">Loading...</p>
      ) : (
        SECTION_KEYS.filter((key) => !sectionFilter || key === sectionFilter).map((key) => {
          const meta = headers[key];
          const items = (sections && sections[key]) || [];
          const filters = filtersBySection[key] || { mode: 'ALL' };
          const isCollapsed = Boolean(collapsedBySection[key]);
          const filteredItems = buildFilteredItems(items, filters.mode);
          const visibleCount = visibleCounts[key] || DEFAULT_VISIBLE_COUNT;
          const visibleItems = filteredItems.slice(0, visibleCount);
          const remainingCount = Math.max(filteredItems.length - visibleItems.length, 0);
          const nextCount = Math.min(SHOW_MORE_STEP, remainingCount);
          const gridTemplateColumns = getGridTemplateColumns(key, meta);
          const emptyCopy = getEmptyCopy(items, filters.mode);

          return (
            <div
              key={key}
              className="recent-submissions__panel"
            >
              <div className="recent-submissions__section-header">
                <button
                  type="button"
                  onClick={() => toggleSection(key)}
                  className="recent-submissions__section-toggle"
                >
                  <span aria-hidden="true">{isCollapsed ? '>' : 'v'}</span>
                  <span>{meta.title}</span>
                  <em>{filteredItems.length}</em>
                </button>
                <div className="recent-submissions__filters">
                  <div className="recent-submissions__mode-tabs">
                    {MODE_TABS.map((tab) => {
                      const isActive = filters.mode === tab.value;
                      return (
                        <button
                          key={tab.value}
                          type="button"
                          onClick={() => updateSectionFilter(key, { mode: tab.value })}
                          className={`recent-submissions__mode-tab ${isActive ? 'is-active' : ''}`}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {isCollapsed ? null : filteredItems.length === 0 ? (
                <div className="recent-submissions__empty">
                  <strong>{emptyCopy.title}</strong>
                  <span>{emptyCopy.text}</span>
                </div>
              ) : (
                <>
                  <div className="recent-submissions__table-wrap">
                    <div
                      className="recent-submissions__table"
                      style={{ '--recent-grid-template': gridTemplateColumns }}
                    >
                      <div className="recent-submissions__table-header">
                        <span>Date</span>
                        {meta.idLabel && <span>{meta.idLabel}</span>}
                        <span>Mode</span>
                        {key === 'enemy' && <span>Team</span>}
                        <span>{meta.label}</span>
                        <span>Map</span>
                        <span>Actions</span>
                      </div>

                      {visibleItems.map((item, idx) => {
                        const itemKey = getItemKey(key, item);
                        const isExpanded = expandedSubmissionId === itemKey;
                        return (
                          <Fragment key={itemKey}>
                            <div
                              className={`recent-submissions__row ${idx % 2 === 0 ? 'is-even' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                            >
                              <span className="recent-submissions__date">{item.date || EMPTY}</span>
                              {meta.idLabel && (
                                <span className="recent-submissions__id">
                                  {item.matchId || item.id || EMPTY}
                                </span>
                              )}
                              <span className="recent-submissions__mode">{getModeLabel(inferModeFromItem(item))}</span>
                              {key === 'enemy' && <span className="recent-submissions__team">{item.team || EMPTY}</span>}
                              <span className="recent-submissions__opponent">
                                {key === 'player' ? (item.opponent || item.vs || EMPTY) : (item.player || item.opponent || item.vs || EMPTY)}
                              </span>
                              <span className="recent-submissions__map">{item.map || item.Map || EMPTY}</span>
                              <span className="recent-submissions__actions">
                                <button
                                  className="recent-submissions__details"
                                  onClick={() => setExpandedSubmissionId(isExpanded ? null : itemKey)}
                                >
                                  {isExpanded ? 'Hide' : 'Details'}
                                </button>
                                <button
                                  className="recent-submissions__edit"
                                  onClick={() => onEdit(item.path)}
                                >
                                  Edit
                                </button>
                                {canDelete && (
                                  <button
                                    className="recent-submissions__delete"
                                    onClick={() => onDelete(item)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </span>
                            </div>
                            {isExpanded && (
                              <div className="recent-submissions__detail-row">
                                {key === 'player' ? (
                                  <>
                                    {renderMatchDetails(item, key)}
                                    {renderPlayerDetails(item)}
                                  </>
                                ) : (
                                  renderMatchDetails(item, key)
                                )}
                              </div>
                            )}
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {remainingCount > 0 && (
                    <div className="recent-submissions__show-more-wrap">
                      <button
                        type="button"
                        className="recent-submissions__show-more"
                        onClick={() => setVisibleCounts((prev) => ({
                          ...prev,
                          [key]: (prev[key] || DEFAULT_VISIBLE_COUNT) + SHOW_MORE_STEP,
                        }))}
                      >
                        Show {nextCount} more
                      </button>
                      <span>{visibleItems.length} of {filteredItems.length} shown</span>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default RecentSubmissions;
